import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Mail, Phone, Building, Loader2, Search } from "lucide-react";
import { fetchContactSubmissions, deleteContactSubmission } from "@/lib/api";
import { formatApiErrorDetail } from "@/contexts/AuthContext";

export default function ContactsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try { setItems(await fetchContactSubmissions()); } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await deleteContactSubmission(id);
      toast.success("Submission deleted");
      if (active?.id === id) setActive(null);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  const filtered = items.filter((c) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return [c.name, c.email, c.subject, c.message, c.company].some((v) => v && v.toLowerCase().includes(q));
  });

  return (
    <div data-testid="admin-contacts">
      <div className="flex items-end justify-between gap-4 mb-7">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900">Contact Inquiries</h1>
          <p className="text-slate-500 mt-1 text-sm">All submissions received via the public contact form.</p>
        </div>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            data-testid="contacts-search"
            className="pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-56"
          />
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-16 rounded-xl bg-slate-100 animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl py-16 text-center">
          <p className="text-slate-500 text-sm">{items.length === 0 ? "No inquiries yet." : "No matches for your search."}</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr,420px] gap-5">
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <ul className="divide-y divide-slate-100">
              {filtered.map((c) => (
                <li key={c.id} data-testid={`contact-row-${c.id}`} onClick={() => setActive(c)}
                  className={`px-5 py-4 cursor-pointer hover:bg-slate-50 flex items-start justify-between gap-3 ${active?.id === c.id ? "bg-blue-50/50" : ""}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900 text-sm truncate">{c.name}</span>
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">{c.subject}</span>
                    </div>
                    <div className="text-xs text-slate-500 truncate mt-0.5">{c.email} · {c.message?.slice(0, 80)}{c.message?.length > 80 ? "…" : ""}</div>
                  </div>
                  <div className="text-xs text-slate-400 whitespace-nowrap">{new Date(c.created_at).toLocaleDateString()}</div>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:sticky lg:top-6 self-start">
            {active ? (
              <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="contact-detail">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-slate-900">{active.name}</h3>
                    <p className="text-xs text-slate-400 mt-0.5">{new Date(active.created_at).toLocaleString()}</p>
                  </div>
                  <button onClick={() => handleDelete(active.id)} data-testid="contact-detail-delete" className="text-slate-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50"><Trash2 size={16} /></button>
                </div>

                <div className="space-y-2.5 text-sm mb-5">
                  <div className="flex items-center gap-2 text-slate-700"><Mail size={14} className="text-slate-400" /> <a href={`mailto:${active.email}`} className="hover:text-blue-600 truncate">{active.email}</a></div>
                  {active.phone && <div className="flex items-center gap-2 text-slate-700"><Phone size={14} className="text-slate-400" /> {active.phone}</div>}
                  {active.company && <div className="flex items-center gap-2 text-slate-700"><Building size={14} className="text-slate-400" /> {active.company}</div>}
                </div>

                <div className="border-t border-slate-100 pt-4">
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Subject</div>
                  <div className="text-sm font-medium text-slate-900 mb-4">{active.subject}</div>
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Message</div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{active.message}</p>
                </div>

                <div className="mt-5 flex items-center gap-2 text-xs">
                  <span className={`px-2 py-1 rounded-full ${active.email_sent ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {active.email_sent ? "Email notification sent" : "Email not sent (Resend disabled)"}
                  </span>
                </div>
              </div>
            ) : (
              <div className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-10 text-center text-sm text-slate-400">
                Select an inquiry to view details
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
